import { TelemetryService } from "../../../ShellCommon/logger/telemetry-service";
import { LoggingService, UserVisibleMessage } from "../loggingSvc";
import { InformativeAlertService } from "../../sdDirectives/informative-alert-service";
import { ServiceDeskConfig } from "../../Startup/serviceDeskConfig";
import { AlertAction } from "../../../ShellCommon/Controls/alert/alert";
import { MsalAuthenticationServiceImplementation } from "../msal/msal-authentication-service";

declare let Toposort: any;

export const ignoreFuturePlsErrorsQueryStringParameter = "ignoreFuturePlsErrors";

export interface RouteLoadingData {
  originalResolve: any;
  deferral: ng.IDeferred<any>;
  stateData: StateData;
  loadPrerequisitesCallback?: (
    originalResolveArgumentNames?: string[],
    originalResolveArguments?: any[],
    originalResolveFn?: () => any
  ) => ng.IPromise<any>;
  prerequisitePromise?: ng.IPromise<any>;
}

export interface StateData {
  name: string;
  data: { requiredBundles?: string[]; requireADLogin?: boolean; resolve?: any };
  skipPrerequisites?: boolean;
  preRequisites?: Set<InputPrerequisite>;
}

export interface Prerequisite {
  localizations?: string[];
  preferences?: { appId: string; sectionId: string }[];
  flights?: boolean[];
  userProfile?: boolean[];
  authorization?: boolean[];
  displayTheme?: boolean[];
  releaseNotesBundle?: Bundle[];
  featureJsonBundle?: Bundle[];
  mockDataConfigBundle?: Bundle[];
  mockOperationsJsonBundle?: Bundle[];
}

export interface PrerequisiteContext {
  routeName: string;
  requiresADLogin: boolean;
}

export interface InputPrerequisite {
  localizations?: string[];
  preferences?: { appId: string; sectionId?: string }[];
  flights?: boolean;
  userProfile?: boolean;
  releaseNotesBundle?: Bundle;
  featureJsonBundle?: Bundle;
  mockDataConfigBundle?: Bundle;
  mockOperationsJsonBundle?: Bundle;
}

export interface Bundle {
  name: string;
  loadPrerequisites?: InputPrerequisite;
  authenticatedGlobalPrerequisites?: InputPrerequisite;
  globalPrerequisites?: InputPrerequisite;
  dependencies?: string[];
  preventLazyLoad?: boolean;
  files: string[];
  loaded?: boolean;
}
const timeoutAlertUID = "PLSTimeoutMessage";

export class PrerequisiteLoadingService {
  private prerequisiteCallbacks = new Set<{
    callback: (params: Prerequisite, context?: PrerequisiteContext) => ng.IPromise<any>;
    name: string;
    loadOnce?: boolean;
    isLoaded?: boolean;
  }>();
  private globalPrerequisites: InputPrerequisite[] = [];
  private authenticatedGlobalPrerequisites: InputPrerequisite[] = [];
  private routeDependencySort = new Map<string, any>();
  private loadMockDataBundles = false;
  private ignoreErrors = false;
  private ignoreTimeouts = false;
  private uiPrerequisteCallbacks: { name: string; callback: (routeName: string) => ng.IPromise<any> }[] = [];
  private ignoreTimeoutsDeferred: ng.IDeferred<any>;
  public hasFinishedLoadingGlobalPrerequisites = false;
  public releaseNotesBundle: Bundle[] = [];

  // @ngInject
  constructor(
    private $q: ng.IQService,
    private $ocLazyLoad: oc.ILazyLoad,
    private $injector: ng.auto.IInjectorService,
    private loggingService: LoggingService,
    private $timeout: ng.ITimeoutService,
    $window: ng.IWindowService,
    private telemetryService: TelemetryService,
    private informativeAlertService: InformativeAlertService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private sdConfig: ServiceDeskConfig
  ) {
    if ($window.location.href.includes(ignoreFuturePlsErrorsQueryStringParameter + "=true")) {
      this.ignoreFutureErrors();
    }
    if ($window.location.href.indexOf("localhost") >= 0 || $window.location.href.indexOf("servicedesk-int") >= 0) {
      this.loadMockDataBundles = true;
    }
    this.ignoreTimeoutsDeferred = this.$q.defer();
  }

  public initializeRoutes(routeDataMap: Map<string, RouteLoadingData>, allBundles: Bundle[]) {
    this.getGlobalDependencies(allBundles);
    this.releaseNotesBundle = allBundles.filter((bundle) => bundle.name.includes("releaseNotes"));
    routeDataMap.forEach((routeData, name) => {
      let stateData = routeData.stateData;
      if (stateData.skipPrerequisites) {
        routeData.loadPrerequisitesCallback = (
          originalResolveArgumentNames?: string[],
          originalResolveArguments?: any[],
          originalResolveFn?: () => any
        ) => {
          return this.loadOriginalResolve(
            originalResolveArgumentNames,
            originalResolveArguments,
            originalResolveFn,
            name
          );
        };
      } else {
        let prerequisites = this.getAllPrerequisitesForState(stateData, allBundles, routeDataMap);
        this.addGlobalPrerequisites(stateData, prerequisites);
        let flattenedPrerequisites = this.flattenPrerequisiteObjects(prerequisites);
        routeData.loadPrerequisitesCallback = this.getLoadPrerequisitesCallback(
          name,
          flattenedPrerequisites,
          routeData,
          allBundles
        ).bind(this);
      }

      if (!routeData.deferral) {
        routeData.deferral = this.$q.defer();
      }
      routeData.deferral.resolve();
    });
  }

  private getGlobalDependencies(allBundles: Bundle[]) {
    allBundles.forEach((bundle) => {
      if (bundle.globalPrerequisites) {
        this.globalPrerequisites.push(bundle.globalPrerequisites);
      }
      if (bundle.authenticatedGlobalPrerequisites) {
        this.authenticatedGlobalPrerequisites.push(bundle.authenticatedGlobalPrerequisites);
      }
      if (bundle.name.includes("releaseNotes")) {
        const input: InputPrerequisite = {};
        input.releaseNotesBundle = bundle;
        this.globalPrerequisites.push(input);
      }
      if (bundle.name.includes("releasedFeaturesJson")) {
        const input: InputPrerequisite = {};
        input.featureJsonBundle = bundle;
        this.globalPrerequisites.push(input);
      }
      if (bundle.name.includes("mockDataConfig") && this.loadMockDataBundles) {
        const input: InputPrerequisite = {};
        input.mockDataConfigBundle = bundle;
        this.globalPrerequisites.push(input);
      }
      if (bundle.name.includes("mockOperationsJson") && this.loadMockDataBundles) {
        const input: InputPrerequisite = {};
        input.mockOperationsJsonBundle = bundle;
        this.globalPrerequisites.push(input);
      }
    });
  }

  /**
   * Register a callback which will be called before a route loads with a prerequite object. It should return a promise which will be resolved when
   * the service finishes loading the prerequisite.
   * @param callback
   * @param name The name of the service that is doing the loading.
   */
  public registerCallbackForPrerequisite(
    name: string,
    callback: (param: Prerequisite, context?: PrerequisiteContext) => ng.IPromise<any>,
    loadOnce = true
  ) {
    this.prerequisiteCallbacks.add({ callback: callback, name: name, loadOnce: loadOnce, isLoaded: false });
  }

  private getLoadPrerequisitesCallback(
    routeName: string,
    prerequisite: Prerequisite,
    routeData: RouteLoadingData,
    allBundles: Bundle[]
  ) {
    let callback = (
      originalResolveArgumentNames?: string[],
      originalResolveArguments?: any[],
      originalResolveFn?: () => any
    ) => {
      try {
        let prerequisiteLoadStartTime = new Date().getTime();
        this.loggingService.logInfo(`Beginning to load prerequisites for route: ${routeName}`);

        routeData.prerequisitePromise =
          routeData.prerequisitePromise || this.loadPrerequisites(prerequisite, routeName, routeData.stateData);
        let allPrerequisitesPromise = routeData.prerequisitePromise
          .then(() => {
            return this.ocLazyLoadDependencies(routeName, allBundles)
              .then(() => {
                return this.loadOriginalResolve(
                  originalResolveArgumentNames,
                  originalResolveArguments,
                  originalResolveFn,
                  routeName
                ).then(() => {
                  return this.loadUiPrerequisites(routeName);
                });
              })
              .catch((error) => {
                this.loggingService.logError(
                  `Error lazy loading dependencies for route '${routeName}'. ${this.stringifyIfNeeded(error)}`
                );
                return this.$q.reject(error);
              });
          })
          .then(() => {
            let prerequisteLoadEndTime = new Date().getTime();
            this.telemetryService.pushContentLoadEventDuration(
              `prerequisite-all-${routeName}`,
              prerequisteLoadEndTime - prerequisiteLoadStartTime,
              `All prerequisites have completed for route ${routeName}`
            );
          })
          .catch((error) => {
            this.loggingService.logError(
              `Error loading prerequisites for route '${routeName}'. ${this.stringifyIfNeeded(error)}`
            );
            return this.handleError(error, routeName);
          });
        return this.wrapPromiseWithTimeout(
          this.sdConfig.totalPrerequisiteServiceLoadTimeout,
          routeName,
          "totalLoadTimeout",
          undefined,
          allPrerequisitesPromise
        );
      } catch (error) {
        this.loggingService.logError(
          `Unhandled error while loading prerequisites for route '${routeName}. ${this.stringifyIfNeeded(error)}`
        );
        return this.handleError(error, routeName);
      }
    };
    return callback;
  }

  private wrapPromiseWithTimeout(
    timeoutLength: number,
    routeName: string,
    timeoutName: string,
    stillLoadingNames: Set<string>,
    promise: ng.IPromise<any>
  ): ng.IPromise<any> {
    let timeoutDeferral = this.$q.defer();
    let logShown = false;
    let timeoutPromise: ng.IPromise<any> = this.$timeout(
      () => {
        let stillLoadingText = stillLoadingNames ? Array.from(stillLoadingNames) + " " : "";
        if (this.sdConfig.isPreProd()) {
          if (this.ignoreTimeouts) {
            timeoutDeferral.resolve("Ignoring PLS timeout");
          } else {
            let errorMessage = `Loading prerequisite(s) ${stillLoadingText}is taking longer than expected.`;
            let alertAction: AlertAction = {
              linkText: "show page anyway",
              callback: (closeAlert: () => void) => {
                this.ignoreTimeouts = true;
                // Resolve any other timeouts that may have been hit already.
                this.ignoreTimeoutsDeferred.resolve("Ignoring future PLS errors");
                closeAlert();
              }
            };
            let userMessage: UserVisibleMessage = {
              displayText: errorMessage,
              title: "Unusually Long Loading Time",
              alertActions: [alertAction],
              uId: timeoutAlertUID
            };
            this.loggingService.logInfo(errorMessage, userMessage);
            logShown = true;
            // A different timeout has already been hit, and ignored. So ignore this one too.
            this.ignoreTimeoutsDeferred.promise.then(() => {
              timeoutDeferral.resolve("Ignoring PLS timeout");
            });
          }
        } else {
          timeoutDeferral.reject(
            `Timeout occurred while loading prerequisite(s) ${stillLoadingText}for route: ${routeName}`
          );
        }
      },
      timeoutLength,
      undefined,
      timeoutName
    );
    promise
      .then((result: any) => {
        this.hasFinishedLoadingGlobalPrerequisites = true;
        timeoutDeferral.resolve(result);
      })
      .catch((error) => {
        timeoutDeferral.reject(error);
      })
      .finally(() => {
        this.$timeout.cancel(timeoutPromise);
        if (logShown) {
          this.informativeAlertService.closeLogByUId(timeoutAlertUID);
        }
      });
    return timeoutDeferral.promise;
  }

  private loadUiPrerequisites(routeName: string): ng.IPromise<any> {
    if (!this.uiPrerequisteCallbacks.length) {
      return this.$q.resolve();
    }
    let stillLoadingPrerequisiteNames: Set<string> = new Set<string>();

    let allPromises: ng.IPromise<any>[] = [];
    this.uiPrerequisteCallbacks.forEach((uiPrerequisite) => {
      stillLoadingPrerequisiteNames.add(uiPrerequisite.name);
      allPromises.push(
        uiPrerequisite.callback(routeName).then(() => {
          stillLoadingPrerequisiteNames.delete(uiPrerequisite.name);
        })
      );
    });
    return this.wrapPromiseWithTimeout(
      this.sdConfig.prerequisiteLoadTimeout,
      routeName,
      "originalResolveTimeout",
      stillLoadingPrerequisiteNames,
      this.$q.all(allPromises)
    );
  }

  public registerCallbackForUiPrerequisite(
    prerequisiteName: string,
    callback: (routeName: string) => ng.IPromise<any>
  ) {
    this.uiPrerequisteCallbacks.push({ name: prerequisiteName, callback: callback });
  }

  private ignoreFutureErrors() {
    if (this.sdConfig.isPreProd()) {
      this.ignoreErrors = true;
    } else {
      this.loggingService.logError("Prerequisite loading service was called to ignore errors in production.");
    }
  }

  private loadOriginalResolve(
    originalResolveArgumentNames: string[],
    originalResolveArguments: any[],
    originalResolveFn: () => any,
    routeName: string
  ): ng.IPromise<any> {
    if (!originalResolveFn) {
      return this.$q.resolve();
    }
    let originalResolveLocals: any = {};
    for (let i = 0; i < originalResolveArgumentNames.length; i++) {
      originalResolveLocals[originalResolveArgumentNames[i]] = originalResolveArguments[i];
    }
    let originalResolveStartTime = new Date().getTime();
    let invokePromise = this.$q
      .when(this.$injector.invoke(originalResolveFn, undefined, originalResolveLocals))
      .then((value) => {
        let originalResolveEndTime = new Date().getTime();
        this.telemetryService.pushContentLoadEventDuration(
          `prerequisite-resolve-${routeName}`,
          originalResolveEndTime - originalResolveStartTime,
          `Successfully invoked resolve function for route: ${routeName}.`
        );
        return value;
      });
    return this.wrapPromiseWithTimeout(
      this.sdConfig.prerequisiteLoadTimeout,
      routeName,
      "originalResolveTimeout",
      new Set(["original resolve"]),
      invokePromise
    );
  }

  private handleError(error: any, routeName: string) {
    if (routeName.startsWith("sdSystem")) {
      return this.$q.resolve("Loading system page. Ignoring error.");
    }
    if (this.ignoreErrors) {
      return this.$q.resolve("Ignoring PLS errors");
    }
    return this.$q.reject(Error(error));
  }

  private stringifyIfNeeded(item: any): string {
    return Object.isObject(item) ? JSON.stringify(item) : item;
  }

  private ocLazyLoadDependencies(routeName: string, allBundles: Bundle[]): ng.IPromise<any> {
    let dependencies = this.routeDependencySort.get(routeName);
    if (dependencies) {
      let lastPromise: ng.IPromise<any>;
      let sortedDependencies = <string[]>dependencies.sort().reverse();
      let currentlyLoadingDependency: string;
      sortedDependencies.forEach((dependency) => {
        let bundle = this.getDependencyBundle(dependency, allBundles);
        if (bundle && bundle.files && bundle.files.length > 0 && !bundle.preventLazyLoad && !bundle.loaded) {
          currentlyLoadingDependency = dependency;
          let promiseCallback = () => {
            let startTime = new Date().getTime();
            return this.$ocLazyLoad
              .load(dependency)
              .then((value) => {
                bundle.loaded = true;
                let endTime = new Date().getTime();
                this.telemetryService.pushContentLoadEventDuration(
                  `prerequisite-dependency-${dependency}`,
                  endTime - startTime,
                  `Prerequisite service loaded dependency: ${dependency}`
                );
                return value;
              })
              .catch((error) => this.$q.reject(error));
          };
          if (lastPromise) {
            lastPromise = this.wrapPromiseWithTimeout(
              this.sdConfig.individualPrerequisiteLoadTimeout,
              routeName,
              "ocLazyLoadDependencyTimeout",
              new Set([dependency]),
              lastPromise.then(promiseCallback.bind(this))
            );
          } else {
            lastPromise = this.wrapPromiseWithTimeout(
              this.sdConfig.individualPrerequisiteLoadTimeout,
              routeName,
              "ocLazyLoadDependencyTimeout",
              new Set([dependency]),
              promiseCallback()
            );
          }
        }
      });
      return lastPromise ? lastPromise : this.$q.resolve();
    }
    return this.$q.resolve();
  }

  private acquireLoginToken(stateData: StateData): ng.IPromise<any> {
    if (stateData.data.requireADLogin != false && !this.msalAuthenticationService.userInfo.isAuthenticated) {
      return this.msalAuthenticationService.acquireTokenSilent(this.sdConfig.msalScopes).catch((error: string) => {
        this.loggingService.logError("Error acquiring msal token prior to prerequisites: " + error);
        this.msalAuthenticationService.loginRedirect();
      });
    }
    return this.$q.resolve();
  }

  private loadPrerequisites(prerequisite: Prerequisite, routeName: string, stateData: StateData): ng.IPromise<any> {
    return this.acquireLoginToken(stateData).then(() => {
      let currentlyLoadingPrerequisites = new Set<string>();
      let allPromises: ng.IPromise<any>[] = Array.from(this.prerequisiteCallbacks, (prerequisiteCallback) => {
        if (!prerequisiteCallback.isLoaded || !prerequisiteCallback.loadOnce) {
          this.loggingService.logInfo(`Started loading prerequisite ${prerequisiteCallback.name}`);
          let startTime = new Date().getTime();
          currentlyLoadingPrerequisites.add(prerequisiteCallback.name);
          prerequisiteCallback.isLoaded = true;
          return prerequisiteCallback
            .callback(prerequisite, { routeName, requiresADLogin: stateData.data.requireADLogin })
            .then((value) => {
              let endTime = new Date().getTime();
              this.telemetryService.pushContentLoadEventDuration(
                `prerequisite-service-${prerequisiteCallback.name}`,
                endTime - startTime,
                `Finished loading prerequisite ${prerequisiteCallback.name}`
              );
              currentlyLoadingPrerequisites.delete(prerequisiteCallback.name);
              return value;
            });
        }
      });
      return this.wrapPromiseWithTimeout(
        this.sdConfig.prerequisiteLoadTimeout,
        routeName,
        "prerequisiteLoadTimeout",
        currentlyLoadingPrerequisites,
        this.$q.all(allPromises)
      );
    });
  }

  private getDependencyBundle(bundleName: string, bundles: Bundle[]): Bundle {
    let dependencyBundle = bundles.find((bundle) => {
      return bundle.name == bundleName;
    });
    if (!dependencyBundle) {
      throw new Error(`Dependency bundle ${bundleName} not found in bundleconfig.`);
    }
    return dependencyBundle;
  }

  private appendPrerequisitesForBundle(
    bundle: Bundle,
    bundles: Bundle[],
    masterPrerequisites: Set<InputPrerequisite>,
    routeName: string
  ) {
    if (bundle.loadPrerequisites) {
      masterPrerequisites.add(bundle.loadPrerequisites);
    }
    if (bundle.preventLazyLoad != true) {
      let dependencySort = this.routeDependencySort.get(routeName);
      if (!dependencySort) {
        dependencySort = new Toposort();
        this.routeDependencySort.set(routeName, dependencySort);
      }
      dependencySort.add(bundle.name, bundle.dependencies || []);
    }
    if (bundle.dependencies) {
      bundle.dependencies.forEach((dependency: string) => {
        this.appendPrerequisitesForBundle(
          this.getDependencyBundle(dependency, bundles),
          bundles,
          masterPrerequisites,
          routeName
        );
      });
    }
  }

  private getAllPrerequisitesForState(
    stateData: StateData,
    bundles: Bundle[],
    routesByName: Map<string, RouteLoadingData>
  ): Set<InputPrerequisite> {
    if (stateData.preRequisites) {
      return stateData.preRequisites;
    }
    let masterPrerequisites = new Set<InputPrerequisite>();
    if (stateData.data.requiredBundles) {
      stateData.data.requiredBundles.forEach((requiredBundle: string) => {
        this.appendPrerequisitesForBundle(
          this.getDependencyBundle(requiredBundle, bundles),
          bundles,
          masterPrerequisites,
          stateData.name
        );
      });
    }
    this.appendPrerequisitesForParents(stateData.name, bundles, routesByName, masterPrerequisites);
    stateData.preRequisites = masterPrerequisites;
    return masterPrerequisites;
  }

  private addGlobalPrerequisites(stateData: StateData, setPrerequisite: Set<InputPrerequisite>) {
    if (stateData.data.requireADLogin != false) {
      this.authenticatedGlobalPrerequisites.forEach((prerequisite) => {
        setPrerequisite.add(prerequisite);
      });
    }
    this.globalPrerequisites.forEach((prerequisite) => {
      setPrerequisite.add(prerequisite);
    });
  }

  private appendPrerequisitesForParents(
    childName: string,
    bundles: Bundle[],
    routesByName: Map<string, RouteLoadingData>,
    masterPrerequisites: Set<InputPrerequisite>
  ) {
    let lastIndexOfPeriod = childName.lastIndexOf(".");
    if (lastIndexOfPeriod != -1) {
      let parentName = childName.slice(0, lastIndexOfPeriod);
      if (routesByName.has(parentName)) {
        let parentPrerequisites = this.getAllPrerequisitesForState(
          routesByName.get(parentName).stateData,
          bundles,
          routesByName
        );
        parentPrerequisites.forEach((preReq) => {
          masterPrerequisites.add(preReq);
        });
      } else {
        this.appendPrerequisitesForParents(parentName, bundles, routesByName, masterPrerequisites);
      }
    }
  }

  private flattenPrerequisiteObjects(prerequisites: Set<any>): Prerequisite {
    let masterPrerequisite: any = {};
    prerequisites.forEach((prerequisite) => {
      Object.keys(prerequisite).forEach((key) => {
        if (angular.isArray(prerequisite[key])) {
          masterPrerequisite[key] = prerequisite[key].concat(masterPrerequisite[key] || []);
        } else {
          masterPrerequisite[key] = [prerequisite[key]].concat(masterPrerequisite[key] || []);
        }
      });
    });
    return masterPrerequisite;
  }
}
